/* Popups storefront script — sections/scratch-popup.liquid.
   Reads the app's published config, picks the eligible popup and a sticky A/B variant, fills the
   markup, runs triggers, the game (scratch card, Plinko or mystery boxes), the optional email step, and reports
   events and claims to the app via the signed app proxy. Everything waits for window load.

   Scratch card: canvas at device pixel ratio, destination-out brush along the pointer path,
   cleared ratio checked per frame, 180ms fade at the threshold (same mechanic as the Alia card).
   Plinko: ported from the Alia Plinko module — 420-wide board, rows of pegs (row n has n+2 pegs,
   58px apart), gravity .3 / restitution .4 / friction .997 / nudge .4, the prize bucket is chosen
   by weight first and the drop is re-simulated until the ball lands in it, then the path is
   played back at one step per frame with a fading trail. */
(() => {
  const root = document.getElementById('ScratchPopup');
  if (!root) return;
  const cfgEl = document.getElementById('ScratchPopupConfig');
  let cfg = null;
  try { cfg = JSON.parse(cfgEl ? cfgEl.textContent : 'null'); } catch (e) { cfg = null; }
  if (!cfg || !Array.isArray(cfg.popups)) return;

  const d = root.dataset;
  const params = new URLSearchParams(location.search);
  const forced = params.get('scratch');
  const design = !!(window.Shopify && window.Shopify.designMode);
  const proxy = (d.proxy || '/apps/popups').replace(/\/$/, '');

  /* ------------------------------------------------------------- storage */
  const ls = { get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } }, set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) {} }, del: (k) => { try { localStorage.removeItem(k); } catch (e) {} } };
  let visitor = ls.get('np_vid');
  if (!visitor) { visitor = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2)); ls.set('np_vid', visitor); }
  const returning = !!ls.get('np_first');
  if (!returning) ls.set('np_first', String(Date.now()));
  if (forced === 'reset') cfg.popups.forEach((p) => ls.del('np_seen:' + p.id));

  /* ------------------------------------------------------ eligibility */
  const eligibleIds = (d.eligible || '').split(',').filter(Boolean);
  const isMobile = matchMedia('(max-width: 899px)').matches || /Mobi|Android|iPhone/i.test(navigator.userAgent);
  let candidates = cfg.popups.filter((p) => {
    if (forced && forced !== '1' && forced !== 'reset') return p.id === forced;
    if (!eligibleIds.includes(p.id)) return false;
    const t = p.targeting || {};
    if (t.devices === 'mobile' && !isMobile) return false;
    if (t.devices === 'desktop' && isMobile) return false;
    if (t.visitors === 'new' && returning) return false;
    if (t.visitors === 'returning' && !returning) return false;
    return true;
  });
  if (!candidates.length) return;
  /* Split tests: a visitor is assigned one popup of a test once (by share) and keeps it. If their
     popup is not for this page they see nothing from that test rather than a sibling, which would
     skew the numbers. Popups outside any test are shown as before. */
  const tests = Array.isArray(cfg.tests) ? cfg.tests : [];
  const inTest = (id) => tests.some((t) => t.popups.some((x) => x.id === id));
  let popup = null;
  if (forced && forced !== '1' && forced !== 'reset') popup = candidates[0];
  for (const t of tests) {
    if (popup) break;
    const here = candidates.filter((p) => t.popups.some((x) => x.id === p.id));
    if (!here.length) continue;
    const key = 'np_test:' + t.id;
    let chosen = ls.get(key);
    if (!chosen || !t.popups.some((x) => x.id === chosen)) {
      const total = t.popups.reduce((a, x) => a + (Number(x.weight) || 0), 0) || 1;
      let r = Math.random() * total; chosen = t.popups[t.popups.length - 1].id;
      for (const x of t.popups) { r -= Number(x.weight) || 0; if (r < 0) { chosen = x.id; break; } }
      ls.set(key, chosen);
    }
    popup = here.find((p) => p.id === chosen) || null;
  }
  if (!popup) popup = candidates.find((p) => !inTest(p.id)) || null;
  if (!popup) return;
  const isPlinko = popup.type === 'plinko';
  const isBoxes = popup.type === 'boxes'; // Ridge-style "pick a box": three mystery boxes, one weighted prize

  /* --------------------------------------------------- variant (sticky) */
  const vKey = 'np_var:' + popup.id;
  let variant = popup.variants.find((v) => v.id === ls.get(vKey));
  if (!variant) {
    let r = Math.random() * 100;
    variant = popup.variants.find((v) => (r -= v.weight) < 0) || popup.variants[popup.variants.length - 1];
    ls.set(vKey, variant.id);
  }
  const C = variant.content || {};
  const D = variant.design || {};
  let wonBucket = -1;
  /* Images: Shopify CDN files are requested at the size this screen needs (sharp, but no 4 MB originals on a
     phone), and everything the popup will show is fetched while the page is idle so opening is instant. */
  const isPhone = window.matchMedia('(max-width: 899px)').matches; // same breakpoint as the CSS
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const need = (cssPx) => Math.min(2400, Math.ceil((cssPx * dpr) / 100) * 100);
  const sized = (url, cssPx) => {
    if (!url || !/(cdn\.shopify\.com|\/cdn\/shop\/)/.test(url)) return url;
    try { const u = new URL(url, location.href); u.searchParams.set('width', String(need(cssPx))); return u.toString(); } catch (e) { return url; }
  };
  const bgCss = isPhone && D.bgImageM ? D.bgImageM : D.bgImage;
  // Phones: the background is a soft spotlight/glow, 1100px is plenty even at 3x — the 1500px file weighed 430 KB on every page.
  const sizedBg = sized(bgCss, isPhone ? Math.min(1100, Math.max(innerWidth, innerHeight)) : Math.max(innerWidth, innerHeight));
  const preloadList = [];

  /* ------------------------------------------------------------- events */
  const send = (path, body, beacon) => {
    const url = proxy + path;
    const json = JSON.stringify(body);
    if (beacon && navigator.sendBeacon) { try { navigator.sendBeacon(url, new Blob([json], { type: 'application/json' })); return Promise.resolve(null); } catch (e) {} }
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: json, keepalive: true }).then((r) => r.json()).catch(() => null);
  };
  const track = (type, extra) => { if (design) return; send('/event', Object.assign({ popup: popup.id, variant: variant.id, visitor, type, page: d.page || location.pathname }, extra || {}), true); };

  /* --------------------------------------------------------------- fill */
  const q = (s) => root.querySelector(s);
  const card = q('[data-sp-card]');
  const canvas = q('[data-sp-cover]');
  const closeBtn = q('[data-sp-close]');
  const emailForm = q('[data-sp-email]');
  const gameEl = q('[data-sp-game]');
  const plinkoEl = q('[data-sp-plinko]');
  const boardEl = q('[data-sp-board]');
  const boardCanvas = q('[data-sp-board-canvas]');
  const dropBtn = q('[data-sp-drop]');
  const wonEl = q('[data-sp-won]');
  const boxesEl = q('[data-sp-boxes]');
  const boxRow = q('[data-sp-boxrow]');
  const claimBtn = isPlinko ? q('[data-sp-claim-plinko]') : isBoxes ? q('[data-sp-claim-boxes]') : q('[data-sp-claim]');
  const codeEl = isPlinko ? q('[data-sp-code-plinko]') : isBoxes ? q('[data-sp-code-boxes]') : q('[data-sp-code]');

  root.style.setProperty('--sp-bg', D.bg || '#fff');
  root.style.setProperty('--sp-text', D.text || '#070707');
  root.style.setProperty('--sp-wave', D.wave || '#f7f7f7');
  root.style.setProperty('--sp-btn', D.button || '#070707');
  root.style.setProperty('--sp-btn-text', D.buttonText || '#fff');
  if (D.font === 'system') root.classList.add('sp--system');
  if (D.font === 'serif') root.classList.add('sp--serif');
  const L = D.layout || 'stack';
  root.classList.add('sp--' + L, 'sp--bg-' + (D.bgType || 'solid'), 'sp--align-' + (D.align || 'center'), 'sp--h-' + (D.headingCase || 'upper'), 'sp--hw-' + (D.headingWeight || 'bold'), 'sp--btn-' + (D.btnShape || 'rounded'), 'sp--btns-' + (D.btnStyle || 'solid'), 'sp--btnc-' + (D.btnCase || 'upper'), 'sp--frames-' + (D.frameStyle || 'tilted'), 'sp--side-' + (D.sidePosition || 'right'));
  if (D.revealStyle === 'plain') root.classList.add('sp--card-plain'); // default: the white card stays exactly as it is
  if (D.grain) {
    root.classList.add('sp--grain');
    root.style.setProperty('--sp-grain', String(Math.max(0, Math.min(1, (D.grainAmount == null ? 45 : D.grainAmount) / 100)) * 0.45));
    const hb = /^#([0-9a-f]{6})$/i.test(D.bg || '') ? D.bg.slice(1) : '0b0c0f';
    const lum = [0, 2, 4].map((i) => parseInt(hb.slice(i, i + 2), 16)).reduce((a, b) => a + b, 0) / 3;
    if (lum > 140) root.classList.add('sp--grain-dark');
  }
  if (D.showWave === false) root.classList.add('sp--nowave');
  root.style.setProperty('--sp-bg2', D.bg2 || '#e9e4d6');
  root.style.setProperty('--sp-bg-angle', (D.bgAngle == null ? 135 : D.bgAngle) + 'deg');
  root.style.setProperty('--sp-bg-overlay', String((D.bgOverlay || 0) / 100));
  root.style.setProperty('--sp-h-size', (D.headingSize || 56) + 'px');
  root.style.setProperty('--sp-card-r', (D.cardRadius == null ? 16 : D.cardRadius) + 'px');
  root.style.setProperty('--sp-btn-ls', ((D.btnSpacing == null ? 6 : D.btnSpacing) / 100) + 'em');
  root.style.setProperty('--sp-cut', (D.cutoutSize || 42) + '%');
  root.style.setProperty('--sp-cut-m', (D.cutoutSizeMobile || 60) + '%');

  root.style.setProperty('--sp-card-bg', D.cardBg || '#ffffff');
  root.style.setProperty('--sp-card-text', D.cardText || '#070707');
  if (D.consentColor) root.style.setProperty('--sp-consent', D.consentColor);
  if (D.cardImage) { const ci = sized(D.cardImage, 640); root.style.setProperty('--sp-card-img', 'url("' + ci + '")'); preloadList.push(ci); }
  if (D.bgFit === 'width' || D.bgFit === 'height') root.classList.add('sp--fit-' + D.bgFit);
  root.style.setProperty('--sp-bg-pos', (D.bgPos || 'center').replace('-', ' '));
  root.style.setProperty('--sp-bg-pos-m', (D.bgPosM || D.bgPos || 'center').replace('-', ' '));
  if (D.bgType === 'image' && sizedBg) { root.style.setProperty('--sp-bg-image', 'url("' + sizedBg + '")'); root.style.setProperty('--sp-bg-image-m', 'url("' + sizedBg + '")'); preloadList.push(sizedBg); }
  root.classList.add('sp--v-' + (D.contentPos || 'center'), 'sp--vm-' + (D.contentPosM || D.contentPos || 'center'));
  root.style.setProperty('--sp-voff', (D.contentOffset || 0) + '%');
  root.style.setProperty('--sp-voff-m', (D.contentOffsetM == null ? (D.contentOffset || 0) : D.contentOffsetM) + '%');
  if (D.bgType === 'glow') {
    const POS = { tr: '75.3% 15.6%', tl: '24.7% 15.6%', tc: '50% 10%', c: '50% 45%', bl: '30.6% 73.1%', br: '69.4% 73.1%', bc: '50% 85%', ml: '8% 50%', mr: '92% 50%' };
    const light = (hex, fallback, strength, dflt, pos, dpos, n) => {
      const h = /^#([0-9a-f]{6})$/i.test(hex || '') ? hex.slice(1) : fallback;
      const rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(',');
      const a = Math.max(0, Math.min(1, (strength == null ? dflt : strength) / 100));
      root.style.setProperty('--sp-glow' + n, 'rgba(' + rgb + ',' + a.toFixed(2) + ')');
      root.style.setProperty('--sp-glow' + n + '-mid', 'rgba(' + rgb + ',' + (a * 0.566).toFixed(2) + ')');
      root.style.setProperty('--sp-g' + (n || '1'), POS[pos] || POS[dpos]);
      return h;
    };
    const h1 = light(D.bg2, '404c53', D.glowStrength, 100, D.glowPos, 'tr', '');
    light(D.bg3, h1, D.glow2Strength, 62, D.glow2Pos, 'bl', '2');
    light(D.bg4, h1, D.glow3Strength, 0, D.glow3Pos, 'mr', '3');
  }
  const sideImg = q('[data-sp-side]');
  if (sideImg && L === 'split') { const su = sized(D.sideImage || (D.images || [])[0], innerWidth / 2); if (su) { sideImg.dataset.spSrc = su; sideImg.hidden = false; preloadList.push(su); } }
  if (L === 'canvas') root.querySelectorAll('[data-sp-cut]').forEach((el) => {
    const i = Number(el.dataset.spCut), n = i + 1, u = (D.images || [])[i];
    if (!u) return;
    const g = (k, dflt) => { const v = D['cut' + n + k]; return v == null || v === '' ? dflt : v; };
    const corner = g('Corner', n === 1 ? 'tr' : 'bl');
    el.classList.add('c-' + corner, 'cm-' + g('CornerM', corner));
    if (g('HideM', false)) el.classList.add('cm-hide');
    el.style.setProperty('--w', g('Size', D.cutoutSize || 42) + '%');
    el.style.setProperty('--wm', g('SizeM', D.cutoutSizeMobile || 60) + '%');
    const rot = g('Rotate', n === 1 ? -22 : 18), ox = g('X', n === 1 ? 3 : 4), oy = g('Y', n === 1 ? 6 : 8);
    el.style.setProperty('--r', rot + 'deg'); el.style.setProperty('--x', ox + '%'); el.style.setProperty('--y', oy + '%');
    // Phone has its own rotation and offsets; unset = what the phone used to derive from desktop (x×2.5, y×0.5).
    el.style.setProperty('--rm', g('RotateM', rot) + 'deg'); el.style.setProperty('--xm', g('XM', Math.round(ox * 2.5)) + '%'); el.style.setProperty('--ym', g('YM', Math.round(oy * 0.5)) + '%');
    const cu = sized(u, Math.ceil(innerWidth * ((isPhone ? g('SizeM', D.cutoutSizeMobile || 60) : g('Size', D.cutoutSize || 42)) / 100)));
    el.dataset.spSrc = cu; el.hidden = false; preloadList.push(cu); // fetched at warm/open, not on page load
  });
  if (isPlinko) { root.classList.add('sp--plinko'); gameEl.hidden = true; plinkoEl.hidden = false; if (D.boardBg) boardEl.style.setProperty('--sp-board-bg', 'url("' + D.boardBg + '")'); }
  if (isBoxes) {
    // Reference (Ridge / Recart "mystery option"): 120px black boxes, 4px #ccc border, radius 8, 0 5px 15px shadow, "?" at 2/3 of the box,
    // one pulse (scale 1.25 at 50%, 1s) when they appear; 80px boxes on phones.
    root.classList.add('sp--boxes'); gameEl.hidden = true; boxesEl.hidden = false;
    root.style.setProperty('--sp-box-d', (D.boxSize || 120) + 'px'); root.style.setProperty('--sp-box-m', (D.boxSizeM || 80) + 'px');
    root.style.setProperty('--sp-box-bg', D.boxBg || '#000000'); root.style.setProperty('--sp-box-text', D.boxText || '#ffffff');
    root.style.setProperty('--sp-box-border', D.boxBorder || '#cccccc'); root.style.setProperty('--sp-box-bw', (D.boxBorderWidth == null ? 4 : D.boxBorderWidth) + 'px'); root.style.setProperty('--sp-box-r', (D.boxRadius == null ? 8 : D.boxRadius) + 'px');
    if (D.boxShadow === false) root.classList.add('sp--box-flat');
    // Reveal look (defaults reproduce the original): opening animation, prize and "You've unlocked" sizes, prize colour, capitals.
    root.classList.add('sp--reveal-' + (['flip', 'burst', 'shake', 'open'].includes(D.boxReveal) ? D.boxReveal : 'grow'));
    const hex = (v) => /^#[0-9a-f]{6}$/i.test(v || '');
    if (hex(D.boxWinBg)) root.style.setProperty('--sp-box-win-bg', D.boxWinBg); if (hex(D.boxWinText)) root.style.setProperty('--sp-box-win-text', D.boxWinText);
    if (hex(D.boxOtherBg)) root.style.setProperty('--sp-box-other-bg', D.boxOtherBg); if (hex(D.boxOtherText)) root.style.setProperty('--sp-box-other-text', D.boxOtherText);
    if (D.prizeSize) root.style.setProperty('--sp-prize', D.prizeSize + 'px');
    if (D.prizeSizeM) root.style.setProperty('--sp-prize-m', D.prizeSizeM + 'px');
    if (D.preSize) root.style.setProperty('--sp-pre', D.preSize + 'px');
    if (D.preSizeM) root.style.setProperty('--sp-pre-m', D.preSizeM + 'px');
    if (/^#[0-9a-f]{6}$/i.test(D.prizeColor || '')) root.style.setProperty('--sp-prize-c', D.prizeColor);
    if (D.preCase === 'none') root.classList.add('sp--pre-asis');
    const nb = Math.max(2, Math.min(5, Number(D.boxCount) || 3));
    for (let i = 0; i < nb; i++) { const b = document.createElement('button'); b.type = 'button'; b.className = 'sp__box'; if (D.boxReveal === 'open') { const fr = document.createElement('span'); fr.className = 'sp__box-f'; fr.textContent = D.boxLabel || '?'; const bk = document.createElement('span'); bk.className = 'sp__box-b'; b.append(fr, bk); } else b.textContent = D.boxLabel || '?'; b.setAttribute('aria-label', 'Mystery box ' + (i + 1) + ' of ' + nb); b.dataset.spBox = String(i); boxRow.append(b); }
  }
  const logo = q('[data-sp-logo]');
  if (D.logoGlow !== false) {
    const gc = /^#([0-9a-f]{6})$/i.test(D.logoGlowColor || '') ? D.logoGlowColor.slice(1) : (/^#([0-9a-f]{6})$/i.test(D.text || '') ? D.text.slice(1) : 'ffffff');
    const grgb = [0, 2, 4].map((i) => parseInt(gc.slice(i, i + 2), 16)).join(',');
    const gs = Math.max(0, Math.min(1, (D.logoGlowStrength == null ? 60 : D.logoGlowStrength) / 100)), gb = D.logoGlowSize || 22;
    root.style.setProperty('--sp-logo-glow', 'drop-shadow(0 0 ' + gb + 'px rgba(' + grgb + ',' + gs.toFixed(2) + ')) drop-shadow(0 0 ' + Math.round(gb / 2) + 'px rgba(' + grgb + ',' + (gs * 0.6).toFixed(2) + '))');
    root.style.setProperty('--sp-brand-glow', '0 0 ' + gb + 'px rgba(' + grgb + ',' + gs.toFixed(2) + '), 0 0 ' + Math.round(gb / 2) + 'px rgba(' + grgb + ',' + (gs * 0.6).toFixed(2) + ')');
  }
  if (D.logo) { logo.src = sized(D.logo, D.logoWidth || 200); preloadList.push(logo.src); logo.style.width = (D.logoWidth || 200) + 'px'; logo.classList.toggle('sp__logo--invert', !!D.logoInvert); logo.hidden = false; }
  else q('[data-sp-brand]').hidden = false;
  q('[data-sp-heading]').textContent = C.heading || '';
  q('[data-sp-sub]').textContent = C.subheading || '';
  q('[data-sp-sub]').hidden = !C.subheading;
  q('[data-sp-pre]').textContent = C.preText || '';
  /* Shrink a prize line until it fits its box (down to a floor), then let it wrap if it still cannot. */
  const fitText = (el, maxPx, minPx) => {
    if (!el || !el.parentElement) return;
    const holder = el.closest('.sp__card, .sp__email, .sp__won') || el.parentElement; // the box that clips, not the flex child that grows with the text
    const box = holder.clientWidth - 36; if (box <= 0) return;
    el.style.whiteSpace = 'nowrap'; el.style.fontSize = '';
    const base = maxPx || parseFloat(getComputedStyle(el).fontSize) || 72;
    const textW = () => { const rg = document.createRange(); rg.selectNodeContents(el); return rg.getBoundingClientRect().width; }; // real ink width, centred text overflows both sides
    let size = base;
    el.style.fontSize = size + 'px';
    while (textW() > box && size > (minPx || 30)) { size -= 2; el.style.fontSize = size + 'px'; }
    if (textW() > box) { el.style.whiteSpace = 'normal'; el.style.lineHeight = '1'; }
  };
  /* Prize label; on box popups set to two lines it breaks at the space nearest the middle (MYSTERY / DISCOUNT). */
  const setPrize = (el, label) => {
    if (!el) return;
    const t = String(label || '').trim();
    const sp = []; for (let i = 0; i < t.length; i++) if (t[i] === ' ') sp.push(i);
    if (!(isBoxes && D.prizeLines === 'two' && sp.length)) { el.textContent = t; return; }
    const mid = t.length / 2; let best = sp[0]; sp.forEach((x) => { if (Math.abs(x - mid) < Math.abs(best - mid)) best = x; });
    el.textContent = ''; el.append(t.slice(0, best), document.createElement('br'), t.slice(best + 1));
  };
  const fitPrizes = () => { fitText(q('[data-sp-win]'), 0, 30); fitText(q('[data-sp-email-win]'), 0, 34); fitText(q('[data-sp-won-prize]'), 0, 26); fitText(q('[data-sp-box-prize]'), 0, 26); };
  window.addEventListener('resize', () => { clearTimeout(fitPrizes._t); fitPrizes._t = setTimeout(fitPrizes, 120); });
  // Random prize (scratch): pick by weight once per visitor and popup, then keep it for the visit.
  if (!isPlinko && (isBoxes || popup.prizesRandom) && Array.isArray(popup.buckets) && popup.buckets.length) { // boxes always draw from the prize table
    const key = 'np_prize:' + popup.id; let idx = Number(ls.get(key));
    if (!Number.isInteger(idx) || idx < 0 || idx >= popup.buckets.length) {
      const total = popup.buckets.reduce((a, b) => a + (Number(b.weight) || 0), 0) || 1; let r = Math.random() * total; idx = popup.buckets.length - 1;
      for (let i = 0; i < popup.buckets.length; i++) { r -= Number(popup.buckets[i].weight) || 0; if (r < 0) { idx = i; break; } }
      ls.set(key, String(idx));
    }
    wonBucket = idx;
  }
  q('[data-sp-win]').textContent = wonBucket >= 0 && !isPlinko ? (popup.buckets[wonBucket].label || '') : (C.prizeText || '');
  claimBtn.textContent = C.claimLabel || 'Claim';
  if (dropBtn) dropBtn.textContent = C.dropLabel || 'Drop Ball';
  const frames = root.querySelectorAll('.sp__frame');
  frames.forEach((fr, i) => {
    const img = fr.querySelector('img');
    const u = (D.images || [])[i];
    if (u && (isPlinko || i < 3)) { img.src = sized(u, 400); img.hidden = false; preloadList.push(img.src); } else fr.style.display = 'none';
  });
  const closeDelay = Number(D.closeDelay == null ? 3.5 : D.closeDelay);
  closeBtn.style.animationDelay = closeDelay + 's';
  root.classList.add('sp--x-' + (D.closePos || 'tl'), 'sp--xm-' + (D.closePosM || 'tr'));
  if (D.closeColor) root.style.setProperty('--sp-x-color', D.closeColor);
  root.style.setProperty('--sp-x-size', (D.closeSize || 44) + 'px');
  root.style.setProperty('--sp-x-r', D.closeShape === 'square' ? '0' : D.closeShape === 'rounded' ? '10px' : '50%');
  if (D.closeBgOpacity > 0 && /^#[0-9a-f]{6}$/i.test(D.closeBg || '')) { const xb = D.closeBg.slice(1); root.style.setProperty('--sp-x-bg', 'rgba(' + [0, 2, 4].map((i) => parseInt(xb.slice(i, i + 2), 16)).join(',') + ',' + (D.closeBgOpacity / 100).toFixed(2) + ')'); }
  if (closeDelay === 0) { closeBtn.style.animation = 'none'; closeBtn.style.opacity = '1'; }

  const email = popup.email || {};
  const emailStep = !!email.enabled;
  if (emailStep) {
    q('[data-sp-email-heading]').textContent = C.emailHeading || '';
    q('[data-sp-email-text]').textContent = C.emailText || '';
    q('[data-sp-email] input').placeholder = C.emailPlaceholder || 'Your email';
    q('[data-sp-email-btn]').textContent = C.emailButton || 'Continue';
    q('[data-sp-consent]').textContent = C.emailConsent || '';
  }
  let capturedEmail = '';
  const phoneBox = q('[data-sp-phone]');
  let revealed = false, playedOnce = false;

  /* ---------------------------------------------------- scratch canvas */
  const brush = Math.max(4, Number(D.brushSize) || 22);
  const pct = Math.min(0.95, Math.max(0.05, (Number(D.revealPct) || 45) / 100));
  const cover = new Image();
  let coverReady = false;
  let ctx = null, ro = null, w = 0, h = 0;

  function draw() {
    if (!ctx) return;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = D.coverFallback || '#c9a94a';
    ctx.fillRect(0, 0, w, h);
    if (coverReady) ctx.drawImage(cover, 0, 0, w, h);
    const label = D.cover ? (C.coverLabel || '').trim() : '';
    if (label) {
      const size = Math.round(h * 0.16);
      ctx.font = '800 ' + size + 'px ' + (getComputedStyle(root).fontFamily || 'sans-serif');
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(255,255,255,.62)';
      ctx.fillText(label, w / 2, h / 2 + size * 0.06);
    }
  }
  function size() {
    const rect = card.getBoundingClientRect();
    w = Math.round(rect.width); h = Math.round(rect.height);
    if (!w || !h) return;
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
    ctx = canvas.getContext('2d', { alpha: true });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    draw();
  }
  function initCanvas() {
    if (ro) return;
    cover.crossOrigin = 'anonymous';
    cover.onload = () => { coverReady = true; draw(); };
    cover.src = D.cover || d.defaultCover;
    size();
    ro = new ResizeObserver(size);
    ro.observe(card);
  }

  let scratching = false, lx = 0, ly = 0, checking = false;
  const point = (e) => { const r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const dot = (x, y) => { ctx.beginPath(); ctx.arc(x, y, brush, 0, Math.PI * 2); ctx.fill(); };
  const line = (x0, y0, x1, y1) => { const dx = x1 - x0, dy = y1 - y0, dist = Math.hypot(dx, dy), step = Math.max(1, brush * 0.4), n = Math.ceil(dist / step); for (let i = 1; i <= n; i++) { const t = i / n; dot(x0 + dx * t, y0 + dy * t); } };
  const cleared = () => { const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data; let e = 0, t = 0; for (let i = 3; i < data.length; i += 8) { t++; if (!data[i]) e++; } return t ? e / t : 0; };
  const check = () => { if (checking) return; checking = true; requestAnimationFrame(() => { checking = false; if (!revealed && cleared() >= pct) reveal(); }); };

  function afterReveal() {
    root.classList.add('is-revealed');
    track('reveal');
    prepareClaim();
    // Reference flow: the revealed prize + Claim button first; the email step (if any) comes when Claim is tapped.
    setTimeout(() => claimBtn.focus({ preventScroll: true }), 420);
    window.dispatchEvent(new CustomEvent('scratch:reveal', { detail: { popup: popup.id, variant: variant.id, bucket: wonBucket } }));
  }
  function reveal() {
    if (revealed) return;
    revealed = true;
    if (root.classList.contains('sp--card-plain')) {
      // card + cover fade out together, then the prize pops in as plain text
      root.classList.add('is-revealed');
      setTimeout(() => card.classList.add('is-melted'), 210);
    } else if (root.classList.contains('sp--card-tight')) {
      // white card eases down to hug the prize text
      const from = card.offsetHeight; card.style.height = from + 'px';
      requestAnimationFrame(() => { root.classList.add('is-revealed'); const prize = card.querySelector('.sp__prize'); card.style.height = (prize.offsetHeight + 52) + 'px'; });
      setTimeout(() => { card.classList.add('is-melted'); card.style.height = ''; }, 800);
    }
    canvas.style.transition = 'opacity 180ms ease'; canvas.style.opacity = '0'; canvas.style.pointerEvents = 'none';
    card.setAttribute('tabindex', '-1'); card.setAttribute('aria-label', 'Reward revealed');
    afterReveal();
  }
  if (!isPlinko && !isBoxes) {
    // A swipe that was meant to scroll the page must not scratch the card: the canvas only arms
    // once the entrance animation is over, and a fast vertical flick right after touch-down is ignored.
    let armedAt = performance.now() + 900, probe = null;
    const begin = (p) => { ctx.globalCompositeOperation = 'destination-out'; ctx.fillStyle = '#000'; scratching = true; lx = p.x; ly = p.y; dot(p.x, p.y); if (!playedOnce) { playedOnce = true; track('scratch'); } check(); };
    canvas.addEventListener('pointerdown', (e) => {
      if (revealed || !ctx || performance.now() < armedAt) return;
      e.preventDefault();
      try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
      const p = point(e);
      if (e.pointerType === 'touch') probe = { x: p.x, y: p.y, cx: e.clientX, cy: e.clientY, t: performance.now() };
      else begin(p);
    });
    canvas.addEventListener('pointermove', (e) => {
      if (probe) {
        const dx = e.clientX - probe.cx, dy = e.clientY - probe.cy, dt = performance.now() - probe.t;
        if (Math.abs(dy) > 14 && Math.abs(dx) < Math.abs(dy) * 0.45 && dt < 140) { probe = null; try { canvas.releasePointerCapture(e.pointerId); } catch (err) {} return; } // vertical flick = scroll intent
        if (Math.hypot(dx, dy) < 3 && dt < 140) return;
        const start = probe; probe = null; begin({ x: start.x, y: start.y });
      }
      if (!scratching) return;
      let events = e.getCoalescedEvents ? e.getCoalescedEvents() : [];
      if (!events.length) events = [e];
      for (const ev of events) { const p = point(ev); line(lx, ly, p.x, p.y); lx = p.x; ly = p.y; }
      check();
    });
    const stop = (e) => { if (probe) { const s = probe; probe = null; begin({ x: s.x, y: s.y }); } scratching = false; if (e) { try { canvas.releasePointerCapture(e.pointerId); } catch (err) {} } if (ctx) ctx.globalCompositeOperation = 'source-over'; };
    canvas.addEventListener('pointerup', stop);
    canvas.addEventListener('pointercancel', () => stop());
    card.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!playedOnce) { playedOnce = true; track('scratch'); } reveal(); } });
  }

  /* -------------------------------------------------------------- plinko */
  const PL = { gravity: 0.3, restitution: 0.4, friction: 0.997, nudge: 0.4, minVy: 0.6 };
  const BW = 420, BUCKET_H = 48, FIRST_Y = 36, BOTTOM_GAP = 30, ROW_GAP = 58, SPREAD = 368;
  const rows = Math.max(5, Math.min(12, Number(D.rows) || 8));
  const BH = FIRST_Y + (rows - 1) * ROW_GAP + BOTTOM_GAP + BUCKET_H;
  const buckets = Array.isArray(popup.buckets) && popup.buckets.length >= 2 ? popup.buckets : [{ label: 'Prize', weight: 1 }, { label: 'Prize', weight: 1 }];
  const ballR = 11, pegR = 5;
  let pegs = [], bctx = null, ballImg = null, anim = 0, run = null, trail = [], ballAlpha = 1, settled = false, highlight = -1;

  function layoutPegs() {
    const out = [], n = SPREAD / rows;
    for (let a = 0; a < rows; a++) {
      const count = a + 2, width = (count - 1) * n, start = BW / 2 - width / 2, y = FIRST_Y + a * ROW_GAP;
      for (let u = 0; u < count; u++) out.push({ x: start + u * n, y });
    }
    return out;
  }
  function pickBucket() {
    const total = buckets.reduce((a, b) => a + (Number(b.weight) || 0), 0) || 1;
    let r = Math.random() * total;
    for (let i = 0; i < buckets.length; i++) { r -= Number(buckets[i].weight) || 0; if (r <= 0) return i; }
    return buckets.length - 1;
  }
  function simulate(x0) {
    const bw = BW / buckets.length;
    let x = x0, y = 16, vx = (Math.random() - 0.5) * 0.4, vy = 1;
    const path = [{ x, y }], floor = BH - BUCKET_H + ballR;
    for (let i = 0; i < 5000 && y < floor; i++) {
      vy += PL.gravity; vx *= PL.friction; x += vx; y += vy;
      if (x - ballR < 0) { x = ballR; vx = Math.abs(vx) * PL.restitution; }
      if (x + ballR > BW) { x = BW - ballR; vx = -Math.abs(vx) * PL.restitution; }
      for (const p of pegs) {
        const dx = x - p.x, dy = y - p.y, dist = Math.sqrt(dx * dx + dy * dy), min = ballR + pegR;
        if (dist < min && dist > 0) {
          const nx = dx / dist, ny = dy / dist, dotp = vx * nx + vy * ny;
          vx = (vx - 2 * dotp * nx) * PL.restitution + (Math.random() - 0.5) * PL.nudge;
          vy = (vy - 2 * dotp * ny) * PL.restitution;
          if (vy < PL.minVy) vy = PL.minVy;
          x += nx * (min - dist); y += ny * (min - dist);
        }
      }
      path.push({ x, y });
    }
    return { path, bucket: Math.min(Math.floor(x / bw), buckets.length - 1) };
  }
  function plan(x0) {
    const target = pickBucket();
    let last = null;
    const deadline = performance.now() + 1000;
    for (let i = 0; i < 100000; i++) {
      last = simulate(x0);
      if (last.bucket === target) return last;
      if (i % 50 === 0 && performance.now() > deadline) break;
    }
    return { path: last.path, bucket: target };
  }
  function wrapText(c, text, max) {
    const words = String(text).split(/\s+/); if (words.length <= 1) return [text];
    const lines = []; let cur = words[0];
    for (let i = 1; i < words.length; i++) { const t = cur + ' ' + words[i]; if (c.measureText(t).width <= max) cur = t; else { lines.push(cur); cur = words[i]; } }
    lines.push(cur); return lines;
  }
  function drawBoard(hl) {
    if (!bctx) return;
    const c = bctx, bw = BW / buckets.length;
    const cs = getComputedStyle(boardEl);
    const font = cs.fontFamily || 'system-ui, sans-serif';
    c.clearRect(0, 0, BW, BH);
    c.fillStyle = D.pegColor || '#070707';
    for (const p of pegs) { c.beginPath(); c.arc(p.x, p.y, pegR, 0, Math.PI * 2); c.fill(); }
    c.textAlign = 'center'; c.textBaseline = 'middle';
    const top = BH - BUCKET_H;
    for (let i = 0; i < buckets.length; i++) {
      const x = i * bw;
      if (D.bucketBg && D.bucketBg !== '#ffffff') { c.fillStyle = D.bucketBg; c.fillRect(x, top, bw, BUCKET_H); }
      c.globalAlpha = 0.15; c.fillStyle = D.bucketText || '#070707'; c.fillRect(x, top, 1, BUCKET_H); c.globalAlpha = 1;
      c.font = (i === hl ? '700 ' : '500 ') + '13px ' + font;
      c.fillStyle = D.bucketText || '#070707';
      const lines = wrapText(c, buckets[i].label, bw - 4), lh = 15.6, y0 = top + (BUCKET_H - lines.length * lh) / 2 + lh / 2;
      lines.forEach((l, k) => c.fillText(l, x + bw / 2, y0 + k * lh));
    }
    c.globalAlpha = 0.2; c.fillStyle = D.bucketText || '#070707'; c.fillRect(BW - 1, top, 1, BUCKET_H); c.globalAlpha = 1;
  }
  function drawBall(x, y) {
    const c = bctx, col = D.ballColor || '#070707';
    for (let i = 0; i < trail.length; i++) { c.globalAlpha = (i / trail.length) * 0.18; c.beginPath(); c.arc(trail[i].x, trail[i].y, ballR * 0.6, 0, Math.PI * 2); c.fillStyle = col; c.fill(); }
    c.globalAlpha = ballAlpha;
    if (ballImg && ballImg.complete && ballImg.naturalWidth) c.drawImage(ballImg, x - ballR, y - ballR, ballR * 2, ballR * 2);
    else { c.beginPath(); c.arc(x, y, ballR, 0, Math.PI * 2); c.fillStyle = col; c.fill(); }
    c.globalAlpha = 1;
  }
  function initBoard() {
    if (bctx) return;
    pegs = layoutPegs();
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    boardCanvas.width = Math.round(BW * dpr); boardCanvas.height = Math.round(BH * dpr);
    boardCanvas.style.aspectRatio = BW + ' / ' + BH;
    bctx = boardCanvas.getContext('2d');
    bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (D.ballImage) { ballImg = new Image(); ballImg.crossOrigin = 'anonymous'; ballImg.src = D.ballImage; }
    drawBoard(-1);
    if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', () => { if (!run) drawBoard(highlight); });
  }
  function drop() {
    if (run || revealed || !bctx) return;
    if (emailStep && email.when === 'before' && !capturedEmail) { showEmail(false); return; }
    if (!playedOnce) { playedOnce = true; track('scratch'); }
    boardEl.classList.add('is-done');
    if (dropBtn) { dropBtn.disabled = true; }
    const x0 = Math.max(ballR, Math.min(BW - ballR, BW / 2 + (Math.random() - 0.5) * 20));
    const p = plan(x0);
    run = { path: p.path, index: 0, bucket: p.bucket };
    trail = []; ballAlpha = 1; settled = false; highlight = -1;
    const frame = () => {
      if (!run) return;
      drawBoard(highlight);
      const path = run.path;
      if (settled) {
        trail.splice(0, 1);
        ballAlpha = Math.max(0, ballAlpha - 0.002);
        if (!trail.length && ballAlpha === 0) { run = null; drawBoard(highlight); return; }
      } else {
        const a = path[Math.min(run.index, path.length - 1)];
        run.index++;
        const b = path[Math.min(run.index, path.length - 1)];
        for (let k = 1; k <= 3; k++) { const t = k / 3; trail.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }); }
        while (trail.length > 40) trail.shift();
        if (run.index >= path.length - 1) { settled = true; highlight = run.bucket; land(run.bucket); }
      }
      const cur = path[Math.min(run.index, path.length - 1)];
      drawBall(cur.x, cur.y);
      anim = requestAnimationFrame(frame);
    };
    anim = requestAnimationFrame(frame);
  }
  function land(idx) {
    wonBucket = idx;
    revealed = true;
    const b = buckets[idx] || {};
    q('[data-sp-won-text]').textContent = C.wonText || 'You won';
    q('[data-sp-won-prize]').textContent = b.label || '';
    requestAnimationFrame(() => fitText(q('[data-sp-won-prize]'), 0, 26));
    setTimeout(() => {
      wonEl.hidden = false;
      if (dropBtn) dropBtn.hidden = true;
      claimBtn.hidden = false;
      afterReveal();
    }, 900);
  }
  if (isPlinko) {
    boardEl.addEventListener('click', drop);
    boardEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drop(); } });
    if (dropBtn) dropBtn.addEventListener('click', drop);
  }

  /* --------------------------------------------------------------- boxes */
  // Reference timing (measured on the Ridge popup): the picked box grows to 1.375x over .3s while the
  // others fade to 10%; ~0.8s after the tap the step swaps to the prize + email form.
  if (isBoxes) {
    let picked = false;
    boxRow.addEventListener('click', (e) => {
      const b = e.target.closest('.sp__box'); if (!b || picked || revealed) return;
      if (emailStep && email.when === 'before' && !capturedEmail) { showEmail(false); return; }
      picked = true;
      if (!playedOnce) { playedOnce = true; track('scratch'); }
      const all = [...boxRow.querySelectorAll('.sp__box')];
      let wait = Math.max(300, Math.min(2500, Number(D.revealDelay) || 800));
      if (D.boxReveal === 'open') {
        // Shake, flip the picked box to show the prize, then flip the others one by one to show what they "had".
        const others = all.filter((x) => x !== b);
        const won = prizeLabel();
        let decoys = String(D.otherPrizes || '').split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
        if (!decoys.length) decoys = (popup.buckets || []).map((x) => x.label).filter((l) => l && l !== won);
        if (!decoys.length) decoys = ['5% off', 'Free shipping'];
        all.forEach((x) => { x.style.animation = 'none'; x.disabled = true; });
        b.classList.add('is-picked', 'is-opening');
        setTimeout(() => { b.classList.remove('is-opening'); b.querySelector('.sp__box-b').textContent = won; b.classList.add('is-open'); }, 550);
        others.forEach((x, i) => setTimeout(() => { x.querySelector('.sp__box-b').textContent = decoys[i % decoys.length]; x.classList.add('is-open', 'is-other'); }, 1250 + i * 320));
        wait = 1250 + others.length * 320 + Math.max(600, Math.min(5000, Number(D.revealHold) || 1800));
      } else {
        all.forEach((x) => { x.style.animation = 'none'; x.classList.add(x === b ? 'is-picked' : 'is-faded'); x.disabled = true; });
      }
      setTimeout(() => {
        revealed = true;
        if (emailStep && email.when === 'after' && !capturedEmail) { boxesEl.hidden = true; afterReveal(); showEmail(true); return; }
        q('[data-sp-box-pre]').textContent = C.wonText || "You've unlocked";
        setPrize(q('[data-sp-box-prize]'), prizeLabel());
        boxRow.hidden = true; q('[data-sp-box-won]').hidden = false; claimBtn.hidden = false;
        requestAnimationFrame(() => fitText(q('[data-sp-box-prize]'), 0, 26));
        afterReveal();
      }, wait);
    });
  }

  /* -------------------------------------------------------------- email */
  const prizeLabel = () => (isPlinko ? ((buckets[wonBucket] || {}).label || '') : (wonBucket >= 0 && popup.buckets && popup.buckets[wonBucket] ? popup.buckets[wonBucket].label : (C.prizeText || '')));
  const fillPrize = (s) => String(s || '').replace(/\{prize\}/gi, prizeLabel());
  function showEmail(afterRevealStep) {
    root.classList.add('is-email');
    emailForm.hidden = false;
    const prizeBox = q('[data-sp-email-prize]');
    if (!afterRevealStep) { gameEl.hidden = true; plinkoEl.hidden = true; boxesEl.hidden = true; prizeBox.hidden = true; }
    else {
      // After the reveal the prize becomes the headline and the game gets out of the way (reference flow).
      root.classList.add('is-email-after');
      gameEl.hidden = true; plinkoEl.hidden = true; boxesEl.hidden = true; claimBtn.hidden = true;
      q('[data-sp-email-pre]').textContent = isPlinko || isBoxes ? (C.wonText || 'You won') : (C.preText || '');
      setPrize(q('[data-sp-email-win]'), prizeLabel());
      requestAnimationFrame(() => fitText(q('[data-sp-email-win]'), 0, 34));
      prizeBox.hidden = false;
      q('[data-sp-email-heading]').hidden = true;
      q('[data-sp-email-text]').textContent = fillPrize(C.emailText);
      q('[data-sp-email-btn]').textContent = fillPrize(C.emailButton || 'Claim {prize}');
    }
    setTimeout(() => emailForm.querySelector('input').focus({ preventScroll: true }), 300);
  }
  function hideEmail() {
    root.classList.remove('is-email', 'is-email-after');
    emailForm.hidden = true; phoneBox.hidden = true;
    if (isPlinko) plinkoEl.hidden = false; else if (isBoxes) boxesEl.hidden = false; else gameEl.hidden = false;
    claimBtn.hidden = !(revealed || !(isPlinko || isBoxes));
  }
  emailForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const input = emailForm.querySelector('input');
    const val = input.value.trim();
    const err = q('[data-sp-error]');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { err.hidden = false; input.focus(); return; }
    err.hidden = true;
    capturedEmail = val;
    track('email');
    input.hidden = true; q('[data-sp-email-btn]').hidden = true; q('[data-sp-consent]').hidden = true; q('[data-sp-email-text]').hidden = true; q('[data-sp-email-heading]').hidden = true;
    if (revealed) prepareClaim();
    if (email.phone) {
      // Save the email right away: someone who stops at the phone step still joins the list and keeps their code.
      if (revealed && !design) Promise.resolve(pending).then(() => send('/claim', claimBody('confirm'), true));
      showPhone(); return;
    }
    afterSignup();
  });
  /* SMS step (reference flow: email → phone → "you're in") */
  const CC = [['US', '1'], ['CA', '1'], ['GB', '44'], ['AU', '61'], ['DE', '49'], ['FR', '33'], ['ES', '34'], ['IT', '39'], ['NL', '31'], ['BE', '32'], ['CH', '41'], ['AT', '43'], ['SE', '46'], ['NO', '47'], ['DK', '45'], ['FI', '358'], ['IE', '353'], ['PT', '351'], ['PL', '48'], ['CZ', '420'], ['RO', '40'], ['HU', '36'], ['GR', '30'], ['MX', '52'], ['BR', '55'], ['AR', '54'], ['CL', '56'], ['CO', '57'], ['NZ', '64'], ['ZA', '27'], ['AE', '971'], ['SA', '966'], ['IL', '972'], ['TR', '90'], ['IN', '91'], ['SG', '65'], ['JP', '81'], ['KR', '82'], ['HK', '852'], ['PH', '63']];
  let capturedPhone = '';
  function showPhone() {
    const sel = q('[data-sp-cc]');
    if (!sel.options.length) {
      const names = typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames([navigator.language || 'en'], { type: 'region' }) : null;
      CC.forEach(([iso, dial]) => { const o = document.createElement('option'); o.value = iso + ':' + dial; o.textContent = (names ? names.of(iso) : iso) + ' +' + dial; sel.append(o); });
      const mine = (d.country || 'US').toUpperCase(); const hit = CC.find(([iso]) => iso === mine); sel.value = (hit ? hit[0] + ':' + hit[1] : 'US:1');
    }
    q('[data-sp-email-prize]').hidden = true;
    q('[data-sp-phone-heading]').textContent = fillPrize(C.phoneHeading || 'Finish signing up for texts to activate {prize}');
    q('[data-sp-phone-text]').textContent = fillPrize(C.phoneText || '');
    q('[data-sp-phone-text]').hidden = !C.phoneText;
    q('[data-sp-phone-input]').placeholder = C.phonePlaceholder || 'Your phone number';
    q('[data-sp-phone-btn]').textContent = fillPrize(C.phoneButton || 'Activate discount');
    q('[data-sp-phone-consent]').textContent = C.phoneConsent || '';
    if (!q('[data-sp-phone-skip]')) {
      // The phone is optional: skipping goes straight to the code, like submitting without a number.
      const skip = document.createElement('button');
      skip.type = 'button';
      skip.setAttribute('data-sp-phone-skip', '');
      skip.textContent = C.phoneSkip || 'No thanks, just email';
      skip.style.cssText = 'align-self:center;margin:-2px 0 0;padding:6px 10px;border:0;background:none;color:inherit;opacity:.75;font:inherit;font-size:14px;text-decoration:underline;text-underline-offset:3px;cursor:pointer';
      skip.addEventListener('click', () => { capturedPhone = ''; phoneBox.hidden = true; afterSignup(); });
      q('[data-sp-phone-btn]').after(skip);
    }
    phoneBox.hidden = false;
    setTimeout(() => q('[data-sp-phone-input]').focus({ preventScroll: true }), 200);
  }
  q('[data-sp-phone-btn]').addEventListener('click', () => {
    const inp = q('[data-sp-phone-input]'), err = q('[data-sp-phone-error]');
    const digits = inp.value.replace(/\D/g, '').replace(/^0+/, '');
    if (digits.length < 6 || digits.length > 14) { err.hidden = false; inp.focus(); return; }
    err.hidden = true;
    capturedPhone = '+' + q('[data-sp-cc]').value.split(':')[1] + digits;
    track('phone');
    phoneBox.hidden = true;
    afterSignup();
  });
  /* Everything captured: either the "you're in" screen (after the reveal) or back to the game (before). */
  function afterSignup() {
    if (revealed && root.classList.contains('is-email-after')) {
      q('[data-sp-email-prize]').hidden = true;
      const done = q('[data-sp-done]');
      q('[data-sp-done-heading]').textContent = fillPrize(C.doneHeading || "You're in!");
      q('[data-sp-done-text]').textContent = fillPrize(C.doneText || '');
      q('[data-sp-done-text]').hidden = !C.doneText;
      done.hidden = false;
      emailForm.append(claimBtn, codeEl);
      claimBtn.textContent = C.claimedLabel || C.claimLabel || 'Continue';
      if (email.showCode === false) codeEl.classList.add('sp__code--hidden');
      root.classList.add('is-finishing');
      claimBtn.hidden = false;
      Promise.resolve(claim()).then(() => { root.classList.remove('is-finishing'); [done, claimBtn, codeEl].forEach((el) => { el.classList.remove('sp__in'); void el.offsetWidth; el.classList.add('sp__in'); }); });
      return;
    }
    if (revealed) prepareClaim();
    hideEmail();
    if (revealed) { claim(); }
    else if (isPlinko) { initBoard(); setTimeout(() => boardEl.focus({ preventScroll: true }), 300); }
    else if (isBoxes) { setTimeout(() => { const fb = boxRow.querySelector('.sp__box'); if (fb) fb.focus({ preventScroll: true }); }, 300); }
    else { initCanvas(); setTimeout(() => card.focus({ preventScroll: true }), 300); }
  }

  /* -------------------------------------------------------------- claim */
  let claimed = !design && ls.get('np_claimed:' + popup.id) === '1', claiming = false, pending = null; // a claim is remembered per browser: no re-open, no teaser
  const claimBody = (stage) => ({ popup: popup.id, variant: variant.id, visitor, email: capturedEmail, phone: capturedPhone, bucket: wonBucket, page: d.page || location.pathname, stage });
  // The code is created the moment the prize is revealed, so the Claim button answers instantly.
  function prepareClaim() {
    if (design || pending || claimed) return;
    pending = send('/claim', claimBody('prepare')); // the email/phone are attached when the claim is confirmed
  }
  async function claim() {
    if (claimed) { if (popup.claimAction === 'go' && popup.claimUrl) location.href = popup.claimUrl; else close(); return; }
    if (claiming) return;
    if (emailStep && !capturedEmail && (email.required || email.when === 'after')) { showEmail(true); return; }
    claiming = true;
    claimBtn.disabled = true; claimBtn.classList.add('is-busy');
    const prev = claimBtn.textContent;
    let code = popup.discount && popup.discount.mode === 'shared' ? popup.discount.code : '';
    let res = null;
    if (!design) {
      res = pending ? await pending : null;
      if (res && res.code) send('/claim', claimBody('confirm'), true);
      else res = await send('/claim', claimBody('confirm'));
    }
    if (res && res.code) code = res.code;
    claiming = false; claimed = true; root.classList.add('is-claimed'); if (!design) ls.set('np_claimed:' + popup.id, '1'); claimBtn.disabled = false; claimBtn.classList.remove('is-busy');
    if (code) {
      try { await navigator.clipboard.writeText(code); } catch (e) {}
      // Apply the code (sets Shopify's discount cookie), then re-render the cart drawer so the discount shows without a reload.
      try {
        const _wb = wonBucket >= 0 && popup.buckets ? popup.buckets[wonBucket] : null;
        const _prizeLabel = (_wb && _wb.label) ? _wb.label : (C.prizeText || '');
        fetch('/discount/' + encodeURIComponent(code), { credentials: 'include', mode: 'no-cors', cache: 'no-store' })
          .then(() => {
            if (_prizeLabel) { try { fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { popup_prize_label: _prizeLabel, popup_prize_code: code } }) }).catch(() => {}); } catch (_e) {} }
            try { return window.theme && window.theme.cart && window.theme.cart.refresh && window.theme.cart.refresh(); } catch (e) {}
          }).catch(() => {});
      } catch (e) {}
      const txt = (C.copiedText || 'Code {code} copied.').split('{code}');
      codeEl.textContent = ''; codeEl.append(txt[0] || '');
      const b = document.createElement('b'); b.textContent = code; codeEl.append(b); codeEl.append(txt.slice(1).join('{code}'));
      codeEl.hidden = false;
    } else if (res && res.error) {
      codeEl.textContent = res.error; codeEl.hidden = false;
    }
    claimBtn.textContent = C.claimedLabel || prev;
    window.dispatchEvent(new CustomEvent('scratch:claim', { detail: { popup: popup.id, variant: variant.id, code, email: capturedEmail, bucket: wonBucket } }));
  }
  claimBtn.addEventListener('click', claim);

  /* --------------------------------------------------------- open/close */
  let opened = false, lastFocus = null, openedAt = 0, leftTracked = false;
  // Where the visitor was when they closed, how they closed, and how long it had been open — one close per visitor per open.
  const closeMeta = (via) => ({ stage: root.classList.contains('is-email') ? 'email' : (revealed ? 'prize' : 'intro'), via, secs: Math.max(0, Math.round((performance.now() - openedAt) / 1000)) });
  window.addEventListener('pagehide', () => { if (opened && !claimed && !leftTracked) { leftTracked = true; track('close', closeMeta('leave')); } });
  const attachSrcs = () => root.querySelectorAll('img[data-sp-src]').forEach((im) => { im.src = im.dataset.spSrc; im.removeAttribute('data-sp-src'); });
  const warm = () => { attachSrcs(); preloadList.concat([d.defaultCover || '']).filter(Boolean).forEach((src) => { const im = new Image(); im.decoding = 'async'; im.src = src; }); };
  // Warm the popup's images while the page is idle — but not on slow or data-saver connections, where those bytes would fight the page.
  const conn = navigator.connection || {};
  const slowNet = !!conn.saveData || /(^|[^4])[23]g$/.test(String(conn.effectiveType || ''));
  if (!slowNet) { let warmed = false; const warmOnce = () => { if (warmed) return; warmed = true; warm(); }; ['pointerdown', 'keydown', 'touchstart', 'wheel'].forEach((n) => window.addEventListener(n, warmOnce, { once: true, passive: true })); window.addEventListener('scroll', function sc() { if (window.scrollY > 0) { window.removeEventListener('scroll', sc); warmOnce(); } }, { passive: true }); } // images warm only once the visitor has touched the page (the popup itself cannot open before that anyway)
  let lastScroll = 0;
  window.addEventListener('scroll', () => { lastScroll = performance.now(); }, { passive: true });
  window.addEventListener('touchmove', () => { lastScroll = performance.now(); }, { passive: true });
  // Largest Contentful Paint stops at the first user input. Opening only after the visitor has moved, scrolled
  // or tapped keeps the popup's big white card out of the LCP measurement (Lighthouse and field data alike);
  // the configured delay still applies — it just cannot fire on a page nobody has touched yet.
  let interacted = false, wantOpen = false;
  const onFirstInput = () => { interacted = true; if (wantOpen) { wantOpen = false; open(); } };
  ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel'].forEach((n) => window.addEventListener(n, onFirstInput, { once: true, passive: true }));
  window.addEventListener('scroll', () => { if (window.scrollY > 0) onFirstInput(); }, { once: true, passive: true });
  function open() {
    if (opened) return;
    if (!interacted && !design && !(forced && forced !== '1' && forced !== 'reset')) { wantOpen = true; return; }
    if (!design && (document.querySelector('.drawer.is-open') || document.body.classList.contains('is-locked'))) { setTimeout(open, 1500); return; } // a cart/menu/search drawer is open: wait, never steal its focus
    attachSrcs();
    openedAt = performance.now(); leftTracked = false;
    if (teaser) teaser.hidden = true;
    if (reopenFromTeaser) { closeBtn.style.animation = 'none'; closeBtn.style.opacity = '1'; } // they have seen it already: no close delay
    setTimeout(fitPrizes, 50); setTimeout(fitPrizes, 700);
    if (performance.now() - lastScroll < 600) { setTimeout(open, 250); return; } // never appear mid-scroll
    opened = true;
    lastFocus = document.activeElement;
    root.hidden = false; root.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    if (!design) ls.set('np_seen:' + popup.id, String(Date.now()));
    track('view');
    if (isPlinko) { initBoard(); if (emailStep && email.when === 'before') { /* asked on first drop */ } setTimeout(() => boardEl.focus({ preventScroll: true }), 600); }
    else if (isBoxes) { if (emailStep && email.when === 'before') showEmail(false); else setTimeout(() => { const fb = boxRow.querySelector('.sp__box'); if (fb) fb.focus({ preventScroll: true }); }, 700); }
    else if (emailStep && email.when === 'before') showEmail(false);
    else { initCanvas(); setTimeout(() => card.focus({ preventScroll: true }), 600); }
    window.dispatchEvent(new CustomEvent('scratch:open', { detail: { popup: popup.id, variant: variant.id } }));
  }
  function close(via) {
    if (!opened) return;
    opened = false;
    if (!claimed && !leftTracked) track('close', closeMeta(via || 'x'));
    if (anim) cancelAnimationFrame(anim);
    root.classList.remove('is-open'); root.hidden = true;
    document.body.style.overflow = '';
    // Give focus back only to something still visible (never the teaser tab, which is hidden while the popup is open);
    // otherwise drop it, so the browser does not jump to the theme's "Skip to content" link.
    const lf = lastFocus;
    if (lf && lf.focus && lf !== teaser && lf !== document.body && lf.offsetParent !== null && !root.contains(lf)) lf.focus({ preventScroll: true });
    else if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    setTimeout(showTeaser, 250);
  }
  closeBtn.addEventListener('click', () => close('x'));

  /* ---------------------------------------------------------- teaser tab */
  const T = popup.teaser || {};
  const teaser = document.querySelector('[data-sp-teaser]');
  const teaserOn = teaser && T.enabled !== false && !design;
  let teaserDismissed = false, reopenFromTeaser = false;
  try { teaserDismissed = sessionStorage.getItem('np_teaser_off:' + popup.id) === '1'; } catch (e) {}
  const drawerOpen = () => {
    if (document.querySelector('cart-drawer.active, .cart-drawer.active, #CartDrawer.active, .drawer.active, details[open] > menu-drawer, .menu-drawer.active, details[open] > summary + .menu-drawer, .mini-cart.is-open, .header__icon--menu[aria-expanded="true"], [data-drawer].is-open, .cart-notification.active, .modal__toggle-open')) return true;
    if (document.body.classList.contains('overflow-hidden') || document.body.classList.contains('is-locked') || document.documentElement.classList.contains('overflow-hidden')) return true;
    // generic: if anything (a drawer, an overlay, a modal) is drawn over the tab, get out of its way
    if (teaser && !teaser.hidden && !teaser.classList.contains('is-covered')) {
      const b = teaser.getBoundingClientRect();
      if (b.width && b.height) { const top = document.elementFromPoint(Math.min(innerWidth - 1, Math.max(0, b.left + b.width / 2)), Math.min(innerHeight - 1, Math.max(0, b.top + b.height / 2))); if (top && !teaser.contains(top) && top !== document.body && top !== document.documentElement) return true; }
    }
    return false;
  };
  let teaserTimer = null;
  const syncTeaser = () => { if (!teaserOn || teaser.hidden) return; teaser.classList.toggle('is-covered', opened || drawerOpen()); };
  function showTeaser() {
    if (!teaserOn || teaserDismissed || claimed || opened) return;
    const phone = window.matchMedia('(max-width: 899px)').matches;
    if ((T.devices === 'desktop' && phone) || (T.devices === 'mobile' && !phone)) return;
    teaser.querySelector('[data-sp-teaser-label]').textContent = T.text || 'Scratch to win';
    teaser.className = 'sp-teaser sp-teaser--font-' + (T.font || 'body') + ' sp-teaser--' + (T.side || 'left') + ' sp-teaser--' + (T.position || 'lower') + ' sp-teaser--' + (T.orientation || 'vertical') + ' sp-teaser--' + (T.shape || 'rounded') + (T.attach === 'float' ? ' sp-teaser--float' : '');
    teaser.style.setProperty('--tz-bg', T.bg || '#000000'); teaser.style.setProperty('--tz-color', T.color || '#ffffff');
    teaser.style.setProperty('--tz-border', T.border || '#ffffff'); teaser.style.setProperty('--tz-bw', (T.borderWidth == null ? 2 : T.borderWidth) + 'px');
    teaser.style.setProperty('--tz-off', (T.offset == null ? 64 : T.offset) + 'px'); teaser.style.setProperty('--tz-size', (T.size || 14) + 'px'); teaser.style.setProperty('--tz-size-m', Math.max(11, (T.size || 14) - 1) + 'px');
    teaser.hidden = false; syncTeaser();
    if (!teaserTimer) { teaserTimer = setInterval(syncTeaser, 350); new MutationObserver(syncTeaser).observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] }); }
  }
  if (teaser) {
    teaser.addEventListener('click', (e) => { if (e.target.closest('[data-sp-teaser-x]')) { e.stopPropagation(); teaser.hidden = true; teaserDismissed = true; try { sessionStorage.setItem('np_teaser_off:' + popup.id, '1'); } catch (err) {} return; } teaser.hidden = true; track('cta'); reopenFromTeaser = true; open(); });
    if (teaserOn && T.showBefore) setTimeout(showTeaser, 800);
  }
  const dismissEl = q('[data-sp-dismiss]');
  const dismissText = C.dismissText === undefined ? "No thanks, I'll pay full price" : C.dismissText; // popups published before this field existed
  if (dismissEl && dismissText) { dismissEl.textContent = dismissText; dismissEl.hidden = false; dismissEl.addEventListener('click', () => close('dismiss')); }
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && opened) close('esc'); });
  root.addEventListener('pointerdown', () => root.classList.remove('is-kb'), true);
  document.addEventListener('keydown', (e) => { if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ' || e.key.startsWith('Arrow')) root.classList.add('is-kb'); }, true);

  /* ----------------------------------------------------------- triggers */
  async function arm() {
    if (forced) { open(); return; }
    const t = popup.trigger || {};
    if (!t.testMode) {
      /* Like Klaviyo / Smooche: the popup itself shows once per browser (or again after N days if set);
         after that only the teaser tab stays, on every page and after every refresh, until the visitor
         claims or dismisses it. A new browser / incognito window starts fresh. */
      if (claimed) return;
      const days = Number(t.frequencyDays) || 0;
      const seenAt = Number(ls.get('np_seen:' + popup.id)) || 0;
      if (seenAt && (days === 0 || Date.now() - seenAt < days * 864e5)) { setTimeout(showTeaser, 400); return; }
    }
    const minCart = Number((popup.targeting || {}).minCart) || 0;
    if (minCart > 0) {
      try { const cart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json()); if ((cart.total_price || 0) / 100 < minCart) return; } catch (e) { return; }
    }
    const delay = Math.max(0, Number(t.delay) || 0) * 1000;
    const timer = setTimeout(open, delay);
    const once = () => { clearTimeout(timer); open(); };
    if (t.exitIntent && matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.documentElement.addEventListener('mouseleave', (e) => { if (e.clientY <= 0) once(); }, { once: true });
    }
    const scrollPct = Number(t.scrollPct) || 0;
    if (scrollPct > 0) {
      const onScroll = () => { const max = document.documentElement.scrollHeight - innerHeight; if (max > 0 && (scrollY / max) * 100 >= scrollPct) { removeEventListener('scroll', onScroll); once(); } };
      addEventListener('scroll', onScroll, { passive: true });
    }
  }
  if (design) {
    document.addEventListener('shopify:section:select', (e) => { if (e.detail && e.detail.sectionId === d.sectionId) open(); });
    document.addEventListener('shopify:section:deselect', (e) => { if (e.detail && e.detail.sectionId === d.sectionId) close(); });
    if (forced) open();
    return;
  }
  if (document.readyState === 'complete') arm(); else window.addEventListener('load', arm, { once: true });
})();
